import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { CHART_COLORS, DONUT_COLORS, DONUT_OTHER_COLOR } from '../typings/chart';
import { ChartPointModel }             from '../typings/intelligence.chart-point';
import { DonutSegmentModel }           from '../typings/intelligence.donut-segment';
import { ChartSeriesModel }            from '../typings/intelligence.chart-series';
import { IntelligenceFinancialFiltersModel } from '../typings/intelligence.financial-filters';
import { IntelligenceFinancialsModel } from '../typings/intelligence.financials';
import { IntelligenceFormatService }   from '../intelligence.format.service';
import { IntelligenceService }         from '../intelligence.service';

@Component({
  selector   : 'wor-intelligence-money-tab',
  template: require('./intelligence.money-tab.component.html')
})
export class MoneyTabComponent implements OnInit, OnDestroy {
  public error   : boolean = false;
  public loading : boolean = true;

  public agingRows      : Array<{ invoice_count : number; key : string; outstanding : number }> = [];
  public payablesRows   : Array<{ invoice_count : number; key : string; outstanding : number }> = [];
  public methodSegments : Array<DonutSegmentModel> = [];
  public mixPoints      : Array<ChartPointModel> = [];
  public paymentsPoints : Array<ChartPointModel> = [];
  public revenuePoints  : Array<ChartPointModel> = [];
  public sourceSegments : Array<DonutSegmentModel> = [];

  public filterOptions : { payment_methods : Array<string>; revenue_sources : Array<string> } = null;
  public filters       : IntelligenceFinancialFiltersModel = { payment_method: '', revenue_source: '' };

  public mixSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 2 ], key: 'labour_revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.LABOUR') },
    { color: CHART_COLORS[ 1 ], key: 'parts_revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.PARTS') },
    { color: CHART_COLORS[ 3 ], key: 'sublet_revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.SUBLET') },
    { color: CHART_COLORS[ 4 ], key: 'other_revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER') }
  ];

  public paymentsSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'payments', label: this.translateService.instant('INTELLIGENCE_SPACE.KPIS.PAYMENTS') }
  ];

  public revenueSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.REVENUE') },
    { color: CHART_COLORS[ 0 ], key: 'cost', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.COST') },
    { color: CHART_COLORS[ 2 ], key: 'gross_profit', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.GROSS_PROFIT') }
  ];

  private loadGeneration : number = 0;
  private methodAmounts  : { [ method : string ] : number } = {};
  private sourceAmounts  : { [ source : string ] : number } = {};
  private subscription   : Subscription;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService       : IntelligenceService,
    private translateService         : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  // These filters narrow client side, so clearing them is instant - no reload.
  clearFilters () : void {
    this.filters = { payment_method: '', revenue_source: '' };

    this.buildSegments();
  }

  hasActiveFilters () : boolean {
    return !!this.filters.payment_method || !!this.filters.revenue_source;
  }

  dayLabel () : string {
    return this.intelligenceService.previousRange
      ? this.intelligenceFormatService.date(this.intelligenceService.range.start_date)
      : '';
  }

  isAgingEmpty () : boolean {
    return !this.agingRows.some(row => row.outstanding > 0);
  }

  isMixEmpty () : boolean {
    return this.pointsEmpty(this.mixPoints);
  }

  isPayablesEmpty () : boolean {
    return !this.payablesRows.some(row => row.outstanding > 0);
  }

  isMethodsEmpty () : boolean {
    return !this.methodSegments.some(segment => segment.value > 0);
  }

  isPaymentsEmpty () : boolean {
    return this.pointsEmpty(this.paymentsPoints);
  }

  isSourcesEmpty () : boolean {
    return !this.sourceSegments.some(segment => segment.value > 0);
  }

  isRevenueEmpty () : boolean {
    return this.pointsEmpty(this.revenuePoints);
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getFinancials()
    .then(( data : IntelligenceFinancialsModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      this.revenuePoints = ( data.series || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: {
          cost        : day.cost,
          gross_profit: day.gross_profit,
          revenue     : day.revenue
        }
      }));

      this.paymentsPoints = ( data.series || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: { payments: day.payments }
      }));

      this.mixPoints = ( data.sales_mix_series || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: {
          labour_revenue: day.labour_revenue,
          other_revenue : day.other_revenue,
          parts_revenue : day.parts_revenue,
          sublet_revenue: day.sublet_revenue
        }
      }));

      this.agingRows = this.buildAgingRows(data.receivables_aging || {});
      this.payablesRows = this.buildAgingRows(data.payables_aging || {});

      this.sourceAmounts = data.revenue_by_source || {};
      this.methodAmounts = data.payments_by_method || {};

      // Rebuilt on every load, so a new period offers the methods and sources that
      // period actually has. The lists come from the unfiltered totals below, never
      // from a narrowed view, so choosing one cannot collapse the list to itself.
      this.filterOptions = {
        payment_methods: Object.keys(this.methodAmounts).sort(),
        revenue_sources: Object.keys(this.sourceAmounts).sort()
      };

      this.dropFiltersMissingFromOptions();
      this.buildSegments();
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  money ( value : number ) : string {
    return this.intelligenceFormatService.money(value);
  }

  paymentsTotal () : number {
    return this.sum(this.visible(this.methodAmounts, this.filters.payment_method));
  }

  restoreDay () : void {
    this.intelligenceService.restoreRange();
  }

  revenueTotal () : number {
    return this.sum(this.visible(this.sourceAmounts, this.filters.revenue_source));
  }

  selectDay ( point : ChartPointModel ) : void {
    this.intelligenceService.selectDay(point.key);
  }

  setFilter ( field : keyof IntelligenceFinancialFiltersModel, value : string ) : void {
    this.filters[ field ] = value;

    this.buildSegments();
  }

  toggleFilter ( field : keyof IntelligenceFinancialFiltersModel, value : string ) : void {
    this.setFilter(field, this.filters[ field ] === value
      ? ''
      : value);
  }

  topMethodLabel () : string {
    return this.topLabel(this.visible(this.methodAmounts, this.filters.payment_method));
  }

  topMethodShare () : string {
    return this.share(this.visible(this.methodAmounts, this.filters.payment_method), this.methodAmounts);
  }

  topSourceLabel () : string {
    return this.topLabel(this.visible(this.sourceAmounts, this.filters.revenue_source));
  }

  topSourceShare () : string {
    return this.share(this.visible(this.sourceAmounts, this.filters.revenue_source), this.sourceAmounts);
  }

  // The endpoint returns a complete split of the period, so narrowing to a single
  // method or source here is the same arithmetic the server would do - and it keeps
  // the slowest query on this tab off the filter path entirely.
  private buildSegments () : void {
    this.methodSegments = this.buildSourceSegments(this.visible(this.methodAmounts, this.filters.payment_method));
    this.sourceSegments = this.buildSourceSegments(this.visible(this.sourceAmounts, this.filters.revenue_source));
  }

  // Same rule as every other filtered tab: a filter lives as long as the period still
  // offers it. No reload is needed when one falls away, because the payload behind
  // these lists is never narrowed by them in the first place - only the dropdown would
  // otherwise sit on a value its own list no longer holds.
  private dropFiltersMissingFromOptions () : void {
    const offered = ( value : string, options : Array<string> ) : string => value && options.indexOf(value) !== -1
      ? value
      : '';

    this.filters = {
      payment_method: offered(this.filters.payment_method, this.filterOptions.payment_methods),
      revenue_source: offered(this.filters.revenue_source, this.filterOptions.revenue_sources)
    };
  }

  private share ( amounts : { [ key : string ] : number }, against : { [ key : string ] : number } ) : string {
    const total = this.sum(against);

    return this.intelligenceFormatService.percent(total
      ? ( this.topAmount(amounts) / total ) * 100
      : 0);
  }

  private sum ( amounts : { [ key : string ] : number } ) : number {
    return Object.keys(amounts).reduce(( running, key ) => running + ( amounts[ key ] || 0 ), 0);
  }

  private topAmount ( amounts : { [ key : string ] : number } ) : number {
    return Object.keys(amounts).reduce(( highest, key ) => Math.max(highest, amounts[ key ] || 0), 0);
  }

  private topLabel ( amounts : { [ key : string ] : number } ) : string {
    return Object.keys(amounts).reduce(( best, key ) => !best || ( amounts[ key ] || 0 ) > ( amounts[ best ] || 0 )
      ? key
      : best, '');
  }

  private visible ( amounts : { [ key : string ] : number }, selected : string ) : { [ key : string ] : number } {
    if ( !selected || amounts[ selected ] === undefined ) {
      return amounts;
    }

    return { [ selected ]: amounts[ selected ] };
  }

  // Sources ranked by revenue; anything past the palette folds into Other.
  private buildSourceSegments ( amounts : { [ source : string ] : number } ) : Array<DonutSegmentModel> {
    const entries = Object.keys(amounts)
      .map(label => ({ amount: amounts[ label ] || 0, label }))
      .sort(( first, second ) => second.amount - first.amount);

    const kept = entries.slice(0, DONUT_COLORS.length);
    const remainder = entries.slice(DONUT_COLORS.length).reduce(( sum, entry ) => sum + entry.amount, 0);

    const segments : Array<DonutSegmentModel> = kept.map(( entry, index ) => ({
      color    : DONUT_COLORS[ index ],
      drillable: true,
      key      : entry.label,
      label    : entry.label,
      value    : entry.amount
    }));

    if ( remainder > 0 ) {
      segments.push({
        color : DONUT_OTHER_COLOR,
        label : this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'),
        others: entries.slice(DONUT_COLORS.length).map(( entry, index ) => ({
          color    : DONUT_COLORS[ index % DONUT_COLORS.length ],
          drillable: true,
          key      : entry.label,
          label    : entry.label,
          value    : entry.amount
        })),
        value : remainder
      });
    }

    return segments;
  }

  private buildAgingRows ( aging : { [ bucket : string ] : { invoice_count : number; outstanding : number } } ) : Array<{ invoice_count : number; key : string; outstanding : number }> {
    return [ 'current', 'days_31_60', 'days_61_90', 'days_90_plus' ].map(key => ({
      invoice_count: aging[ key ] ? aging[ key ].invoice_count : 0,
      key,
      outstanding  : aging[ key ] ? aging[ key ].outstanding : 0
    }));
  }

  private pointsEmpty ( points : Array<ChartPointModel> ) : boolean {
    return !points.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }
}
